import { AGENT_STATUS_HINT, AGENT_STATUS_LABEL } from "../../shared/agent-status";
import { PLATFORM_ASSISTANT_SLUG, type AgentDTO, type AgentStatus, type ScheduleDTO } from "../../shared/types";

/** What the person looking may do with one schedule: the buttons a list row offers, as the server would allow them. */
export type ScheduleActions = { canEdit: boolean; canRun: boolean; canPause: boolean; canResume: boolean; canDelete: boolean };

/**
 * A person's schedule is theirs to run, change, pause and resume (D6); an owner of its agent may pause or delete it (D5).
 * One that runs as the agent itself is its agent's owners' in full, Resume included, and nobody else's (AIT-56).
 * `ownsAgent`: whether the person looking owns the schedule's agent.
 */
export function scheduleActions(s: Pick<ScheduleDTO, "ownerUserId" | "runsAs" | "enabled">, meId: string, ownsAgent: boolean): ScheduleActions {
  if (s.runsAs === "agent") return { canEdit: ownsAgent, canRun: ownsAgent, canPause: ownsAgent, canResume: ownsAgent, canDelete: ownsAgent };
  const mine = s.ownerUserId === meId;
  return { canEdit: mine, canRun: mine, canPause: mine || (s.enabled && ownsAgent), canResume: mine, canDelete: mine || ownsAgent };
}

/**
 * Whether the person looking may hand a schedule to someone (AIT-62), as the server would let them: the owner of a
 * person's schedule when that is them and they are an admin, or one of the agent's owners for one that runs as the agent
 * itself — never while it is offered already. Who offered it, or an owner of its agent, withdraws the offer.
 */
export function handoverActions(s: Pick<ScheduleDTO, "ownerUserId" | "runsAs" | "offer">, me: { id: string; role: string }, ownsAgent: boolean) {
  const mayOffer = s.runsAs === "agent" ? ownsAgent : s.ownerUserId === me.id && me.role === "admin";
  return { canOffer: mayOffer && !s.offer, canWithdraw: Boolean(s.offer && (s.offer.byUserId === me.id || ownsAgent)) };
}

/** Who a schedule runs as, on its row's tag (AIT-59): "Runs as Product Manager" for the agent's own, else its person. */
export function runsAsWords(s: Pick<ScheduleDTO, "runsAs" | "agentName" | "ownerName">): string {
  return s.runsAs === "agent" ? `Runs as ${s.agentName ?? "the agent"}` : `Runs as ${s.ownerName ?? "someone who has left"}`;
}

/**
 * Whether the person looking chooses "Runs as" in the dialog (AIT-59), as the server would let them switch it: one of
 * the agent's owners, on an agent that may have schedules of its own and is on, and — on an existing schedule — only
 * one that is theirs or already the agent's. Everyone else reads it.
 */
export function mayChooseRunsAs(
  agent: Pick<AgentDTO, "youAnswerFor" | "isPrimary" | "slug" | "enabled" | "archivedAt">,
  schedule: Pick<ScheduleDTO, "runsAs" | "ownerUserId"> | null,
  meId: string,
): boolean {
  if (agent.youAnswerFor !== true || agent.isPrimary || agent.slug === PLATFORM_ASSISTANT_SLUG || !agent.enabled || agent.archivedAt) return false;
  return !schedule || schedule.runsAs === "agent" || schedule.ownerUserId === meId;
}

/** An agent's schedules in one list (AIT-59): the next to fire first, then the ones run by hand, the paused ones last. */
export function inFiringOrder<T extends Pick<ScheduleDTO, "enabled" | "nextRunAt" | "name">>(schedules: T[]): T[] {
  const rank = (s: T) => (!s.enabled ? 2 : s.nextRunAt ? 0 : 1);
  return [...schedules].sort((a, b) => rank(a) - rank(b) || (a.enabled && a.nextRunAt && b.nextRunAt ? Date.parse(a.nextRunAt) - Date.parse(b.nextRunAt) : 0) || a.name.localeCompare(b.name));
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** A moment's calendar day and clock time in a timezone (the viewer's own when none is given), the same on every engine. */
function inZone(at: Date, timeZone?: string) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(at)
      .map((x) => [x.type, x.value]),
  );
  const [year, month, day] = [Number(p.year), Number(p.month), Number(p.day)];
  const date = Date.UTC(year, month - 1, day);
  return { year, month: MONTHS[month - 1]!, day, weekday: WEEKDAYS[new Date(date).getUTCDay()]!, date, time: `${p.hour}:${p.minute}` };
}

/** When an agent next wakes itself up, short, in the viewer's own time (AIT-61): "Mon 08:00" within the week, "4 Nov" further out. */
export function nextRunShort(iso: string, now = new Date(), timeZone?: string): string {
  const at = inZone(new Date(iso), timeZone);
  const today = inZone(now, timeZone);
  const days = (at.date - today.date) / 86_400_000;
  if (days >= 0 && days < 7) return `${at.weekday.slice(0, 3)} ${at.time}`;
  return `${at.day} ${at.month.slice(0, 3)}${at.year === today.year ? "" : ` ${at.year}`}`;
}

/** The same time written in full, for the status's hint: "Monday 3 November at 08:00". */
export function nextRunFull(iso: string, now = new Date(), timeZone?: string): string {
  const at = inZone(new Date(iso), timeZone);
  return `${at.weekday} ${at.day} ${at.month}${at.year === inZone(now, timeZone).year ? "" : ` ${at.year}`} at ${at.time}`;
}

/** An agent's status as its chip and hint say it (AIT-61): a scheduled agent carries its next run, "Scheduled · Mon 08:00". */
export function agentStatusWords(status: AgentStatus, nextRunAt: string | null, now = new Date(), timeZone?: string): { label: string; hint: string } {
  const label = AGENT_STATUS_LABEL[status];
  const hint = AGENT_STATUS_HINT[status];
  if (status !== "scheduled" || !nextRunAt) return { label, hint };
  return { label: `${label} · ${nextRunShort(nextRunAt, now, timeZone)}`, hint: `${hint} Next run: ${nextRunFull(nextRunAt, now, timeZone)}.` };
}
